import { Injectable } from '@angular/core';
import { StateFacade } from '@symphony-solo/domain/feature-states-aggregator/src';
import { roundTwoDecimals } from '@symphony-solo/shared/utils/src';
import { Observable } from 'rxjs';
import { first, map, withLatestFrom } from 'rxjs/operators';
import { TransactionFacade } from '../+state/transaction.facade';
import { TransactionBuilder } from '../builders';
import { TransactionDto, TransactionLineProductDto } from '../dtos';
import { TransactionType } from '../enums';
import { TransactionLineProductGeneratorService } from './transaction-line-product-generator.service';
import { TransactionTaxRateGeneratorService } from './transaction-tax-rate-generator.service';

@Injectable()
export class TransactionGeneratorService {
  constructor(
    private stateFacade: StateFacade,
    private transactionFacade: TransactionFacade,
    private transactionLineProductGeneratorService: TransactionLineProductGeneratorService,
    private transactionTaxRateGeneratorService: TransactionTaxRateGeneratorService
  ) {}

  public createTransaction(): Observable<TransactionDto> {
    return this.stateFacade.aggregatedState$.pipe(
      withLatestFrom(this.transactionFacade.transactionState$, this.transactionFacade.totalPrice$, this.transactionFacade.totalTax$),
      map(([state, transactionState, totalPrice, totalTax]) => {
        const customerGroup = state.customerGroupsFeature.selectedCustomerGroup;
        const products = transactionState.products;
        const payment = state.paymentsFeature.selectedPayment ?? state.paymentsFeature.payments[0];
        const modifiersDictionary = state.modifiersFeature.modifiers;
        const priceLevelsDictionary = state.priceLevelsFeature.priceLevels;

        // transactionLineProducts
        const transactionLineProducts: TransactionLineProductDto[] = [];
        products.forEach((product) => {
          const transactionLineProduct = this.transactionLineProductGeneratorService.createTransactionLineProduct(
            product,
            modifiersDictionary,
            priceLevelsDictionary
          );
          transactionLineProducts.push(transactionLineProduct);
        });

        if (transactionState.isDeletingInProgress) {
          transactionLineProducts.forEach((transactionLineProduct) => {
            transactionLineProduct.lineIsCorrected = true;
          });
        }

        // transactionLineTaxes
        const transactionTaxes = this.transactionTaxRateGeneratorService.createTransactionTaxRateList(products);

        // prices
        let grossTotal = 0;
        let taxTotal = 0;
        let netTotal = 0;
        if (!transactionState.isDeletingInProgress) {
          grossTotal = parseFloat(totalPrice);
          taxTotal = parseFloat(totalTax);
          netTotal = roundTwoDecimals(grossTotal - taxTotal);
        }

        const transactionDto = new TransactionBuilder()
          .setTransactionID(transactionState.transactionId)
          .setTakeAway(customerGroup.takeaway)
          .setTransactionType(TransactionType.Sale)
          .setCustomerGroup(customerGroup.id, customerGroup.name)
          .setTransactionProducts(transactionLineProducts)
          .addTransactionLineTenders(payment, grossTotal, transactionState.isDeletingInProgress)
          .addTaxes(transactionTaxes)
          .setNetTotal(netTotal)
          .setTaxTotal(taxTotal)
          .setGrossTotal(grossTotal)
          .build();
        return transactionDto;
      }),
      first()
    );
  }
}
